import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { type EntityATI } from '@atlassian/search-client/common/types';
import {
	type TProductDataRegions as ProductDataRegions,
	type TProductEdition as ProductEdition,
} from '@atlassian/search-client/fetch-search-config';
import { extractTraceInfoFromResponse } from '@atlassian/search-client/network-trace-info';
import { type NetworkCallbacks } from '@atlassian/search-client/persist-refresh';
import {
	type SearchPageQueryVariables,
	type ThirdPartyFilters,
} from '@atlassian/search-client/query-generator';
import { searchDialogQuery } from '@atlassian/search-client/search-page';
import { type SearchPageNode } from '@atlassian/search-client/search-page/types';
import { getEdgeRegionHint } from '@atlassian/search-common/get-edge-region-hint';
import { getBackendExperiment } from '@atlassian/search-experiment';
import { type SearchExperimentLayer } from '@atlassian/search-experiment/dynamic/types';
import { type BackendExperiment, type ExperimentConfig } from '@atlassian/search-experiment/types';

import {
	DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
	JIRA_BOARD_ENTITY_ATI,
	JIRA_DASHBOARD_ENTITY_ATI,
	JIRA_FILTER_ENTITY_ATI,
	JIRA_ISSUE_ENTITY_ATI,
	JIRA_PLAN_ENTITY_ATI,
	JIRA_PROJECT_ENTITY_ATI,
} from '../../../../common/constants/entities';
import { type PrimaryProductKey, ProductKeys3P } from '../../../../common/constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import { type SelectedFiltersType } from '../../../../common/constants/types';
import {
	getUserShardExperimentFlags,
	getUserShardPageL1Id,
	getUserShardSpaceL1Id,
	serveUserShardControl,
	serveUserShardModel,
	serveUserShardUI,
} from '../../../../common/utils/experiments';
import { getEmptySelectedFilters, getThirdPartyFilters } from '../../../../common/utils/filters';
import { isHello } from '../../../../common/utils/is-hello';
import type { UseTypeEntitiesResult } from '../../../store/search/get-type-entities';

import {
	validateAssetsResponse,
	validateAtlasResponse,
	validateConfluenceEntitiesResponse,
	validateConfluenceSpacesResponse,
	validateJiraEntitiesResponse,
	validateJiraIssuesResponse,
	validateMercuryResponse,
	validateTalentResponse,
} from './flat-validators';

const experience = 'quick-find';
const userShardAbTestId = 'quick-find-user-shard-search-experiment';

/**
 * Builds merged headers combining atl-attribution and region steering headers.
 * Feature flag check for atl-attribution is handled internally by buildAtlAttributionHeaders.
 */
const buildMergedHeaders = (
	cloudId: string,
	activationId: string | undefined,
	sourceProduct: PrimaryProductKey | undefined,
	productDataRegions: ProductDataRegions | undefined,
): Record<string, string> | undefined => {
	// Feature flag check is handled internally by buildAtlAttributionHeaders
	const atlAttributionHeaders = buildAtlAttributionHeaders({
		cloudId,
		activationId,
		product: sourceProduct,
	});

	const regionSteeringHeaders =
		sourceProduct && productDataRegions
			? { 'atl-edge-region-hint': getEdgeRegionHint(sourceProduct, productDataRegions) }
			: undefined;

	if (!atlAttributionHeaders && !regionSteeringHeaders) {
		return undefined;
	}

	return {
		...regionSteeringHeaders,
		...atlAttributionHeaders,
	};
};

export const getThirdPartyFiltersForQuickFind: (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => ThirdPartyFilters = (thirdPartyConfigs: ThirdPartyConfigsBootstrap) => {
	const excluded3PProducts: ProductKeys3P[] = [
		ProductKeys3P.Slack,
		ProductKeys3P.Teams,
		ProductKeys3P.Gmail,
		ProductKeys3P.OutlookMail,
	];

	// Adding additional 3P products not included in thirdPartyConfigs
	const additional3PProducts: ProductKeys3P[] = [
		ProductKeys3P.Loom, // Loom is 1P product but treated as 3P in API filters
	];

	const thirdPartyKeys = Object.keys(thirdPartyConfigs) as ProductKeys3P[];

	const quickFind3PProducts: ProductKeys3P[] = additional3PProducts
		.concat(thirdPartyKeys)
		.filter((productKey) => !excluded3PProducts.includes(productKey));

	return getThirdPartyFilters(
		quickFind3PProducts,
		false, // titleMatchOnly value
		[], // it will search all subtypes if no entities are provided
		thirdPartyConfigs,
	);
};

const getUserShardExperimentOverrides = ({
	isUserShardModel,
	isUserShardControl,
	experimentLayers,
	experimentId,
	shadowExperimentId,
	userShardL1LayerId,
}: {
	isUserShardModel: boolean;
	isUserShardControl: boolean;
	experimentLayers?: SearchExperimentLayer[];
	experimentId?: string | null;
	shadowExperimentId?: string | null;
	userShardL1LayerId: string;
}): {
	finalExperimentId: string | undefined;
	finalShadowExperimentId: string | undefined;
	finalExperimentLayers?: SearchExperimentLayer[];
} => {
	const l2LayerId = isUserShardModel ? 'L2-pipelineOnly' : 'default';
	const finalExperimentId = isUserShardModel ? 'L1-usershard' : (experimentId ?? undefined);
	const finalShadowExperimentId = shadowExperimentId ?? undefined;
	const finalExperimentLayers =
		isUserShardModel || isUserShardControl
			? [
					{
						name: 'search_platform_layer_l1',
						layerId: null,
						shadowId: null,
						definitions: [
							{
								layerId: userShardL1LayerId,
								entity: '*',
								subEntity: '*',
								shadowId: null,
								abTestId: userShardAbTestId,
							},
						],
						analyticsDefinitions: [
							{
								layerId: userShardL1LayerId,
								entity: '*',
								subEntity: '*',
								shadowId: null,
								abTestId: userShardAbTestId,
								controlId: 'default',
							},
						],
					},
					{
						name: 'search_platform_layer_l2',
						layerId: null,
						shadowId: null,
						definitions: [
							{
								layerId: l2LayerId,
								entity: '*',
								subEntity: '*',
								shadowId: null,
								abTestId: userShardAbTestId,
							},
						],
						analyticsDefinitions: [
							{
								layerId: l2LayerId,
								entity: '*',
								subEntity: '*',
								shadowId: null,
								abTestId: userShardAbTestId,
								controlId: 'default',
							},
						],
					},
				]
			: experimentLayers;

	return { finalExperimentId, finalShadowExperimentId, finalExperimentLayers };
};

// Experiment cohorts explained here: https://hello.atlassian.net/wiki/spaces/CentAI/pages/6112212590/Experiment+Milestones+for+Adding+3Ps+to+Search+Results
const serve3PinQuickFindTest1 = (prerequisiteConditions: boolean) => {
	return (
		prerequisiteConditions &&
		FeatureGates.getExperimentValue<string>(
			'quick_find_3p_search_results_experiment',
			'cohort',
			'control',
		) === 'test-1'
	);
};

// Experiment cohorts explained here: https://hello.atlassian.net/wiki/spaces/CentAI/pages/6112212590/Experiment+Milestones+for+Adding+3Ps+to+Search+Results
const serve3PinQuickFindTest2 = (prerequisiteConditions: boolean) => {
	return (
		prerequisiteConditions &&
		FeatureGates.getExperimentValue<string>(
			'quick_find_3p_search_results_experiment',
			'cohort',
			'control',
		) === 'test-2'
	);
};

export const serve3PinQuickFind = (thirdPartyConfigs: ThirdPartyConfigsBootstrap) => {
	// Pre-requisite 1: We should not request 3P in Quick Find if the user is enrolled in the User Shard experiment
	// Context:
	// * https://atlassian.slack.com/archives/C050EC6FM28/p1762995060918559?thread_ts=1762980919.126129&cid=C050EC6FM28
	// * https://atlassian.slack.com/archives/C085K9CRCCS/p1762988464471699
	const { cohort: userShardCohort } = getUserShardExperimentFlags();
	const isNotEnrolledInUserShard = userShardCohort === undefined;

	// Prerequisite 2: We should not request 3P in Quick Find if the user does not have any 3P authenticated
	const isEligible3PUser = Object.keys(thirdPartyConfigs).some(
		(productKey) => thirdPartyConfigs[productKey]?.userNeedsOAuth === false,
	);

	const prerequisiteConditions = isNotEnrolledInUserShard && isEligible3PUser;

	const serveTest1 = serve3PinQuickFindTest1(prerequisiteConditions);
	const serveTest2 = serve3PinQuickFindTest2(prerequisiteConditions);

	return {
		serveThirdPartyFilters: serveTest1 || serveTest2,
		serveGraphDocument: serveTest1 || serveTest2,
		serveGraphSmartlinks: serveTest1 || serveTest2,
		skip3pHydration: serveTest1 || serveTest2,
		serveAdditionalGraphEntities: serveTest1,
	};
};

const GITHUB_PRODUCT_KEYS: ProductKeys3P[] = [
	ProductKeys3P.Github,
	ProductKeys3P.ForgePioGithubDev,
	ProductKeys3P.ForgePioGithubProd,
];

export const serveGitHub3PInJiraQuickFind = (thirdPartyConfigs: ThirdPartyConfigsBootstrap) => {
	// Prerequisite 1: User should not be enrolled in User Shard experiment
	const { cohort: userShardCohort } = getUserShardExperimentFlags();
	const isNotEnrolledInUserShard = userShardCohort === undefined;

	// Prerequisite 2: User must have at least one GitHub connector authenticated
	const hasGitHubConnected = GITHUB_PRODUCT_KEYS.some(
		(key) => thirdPartyConfigs[key]?.userNeedsOAuth === false,
	);

	const prerequisiteConditions = isNotEnrolledInUserShard && hasGitHubConnected;

	const isEnabled =
		prerequisiteConditions &&
		FeatureGates.getExperimentValue<string>(
			'github_in_jira_search_results_experiment',
			'isEnabled',
			'false',
		) === 'true';

	return { serveGitHub3P: isEnabled };
};

export const getGitHubThirdPartyFiltersForQuickFind: (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => ThirdPartyFilters = (thirdPartyConfigs: ThirdPartyConfigsBootstrap) => {
	const gitHubThirdPartyConfigs: ThirdPartyConfigsBootstrap = {};
	for (const key of GITHUB_PRODUCT_KEYS) {
		if (thirdPartyConfigs[key]) {
			gitHubThirdPartyConfigs[key] = thirdPartyConfigs[key];
		}
	}

	return getThirdPartyFilters(
		GITHUB_PRODUCT_KEYS,
		false, // titleMatchOnly value
		[], // search all subtypes
		gitHubThirdPartyConfigs,
	);
};

export const fetchConfluenceAnd3PEntities: (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	filteredEntities: string[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchEntitiesRequestId: (string | null) | undefined;
	fetchEntitiesTraceId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
	autocompleteSuggestions: string[] | undefined;
}> = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	filteredEntities: string[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	const { commonFilters, confluenceFilters, mercuryFilters, compassFilters, assetsFilters } =
		selectedFilters || getEmptySelectedFilters();

	const isUserShardModel = serveUserShardModel();
	const isUserShardUI = serveUserShardUI();
	const isUserShardControl = serveUserShardControl();
	const userShardPageL1Id = getUserShardPageL1Id();
	const quickFind3PExperiment = serve3PinQuickFind(thirdPartyConfigs);
	const userShardL1LayerId = isUserShardModel
		? (userShardPageL1Id ?? 'L1-usershard-shosexp')
		: 'default';

	const thirdPartyFilters: ThirdPartyFilters = quickFind3PExperiment.serveThirdPartyFilters
		? getThirdPartyFiltersForQuickFind(thirdPartyConfigs)
		: { titleMatchOnly: true, subtypes: [] };

	const supportedEntities = [
		'ati:cloud:confluence:page',
		'ati:cloud:confluence:blogpost',
		'ati:cloud:confluence:attachment',
		'ati:cloud:confluence:whiteboard',
		'ati:cloud:confluence:database',
		'ati:cloud:confluence:embed',
		'ati:cloud:confluence:folder',
		...(isUserShardModel || quickFind3PExperiment.serveGraphDocument
			? ['ati:cloud:graph:document']
			: []),
		...(fg('quickfind-smartlinks') || quickFind3PExperiment.serveGraphSmartlinks
			? ['ati:cloud:graph:remote-link']
			: []),
		...(quickFind3PExperiment.serveAdditionalGraphEntities
			? [
					'ati:cloud:graph:branch',
					'ati:cloud:graph:calendar-event',
					'ati:cloud:graph:design',
					'ati:cloud:graph:pull-request',
					'ati:cloud:graph:repository',
					'ati:cloud:graph:video',
					'ati:cloud:graph:work-item',
					// These entities are excluded to reduce the noise of 3p results in QF (https://hello.atlassian.net/wiki/x/SDhKYgE)
					// 'ati:cloud:graph:conversation',
					// 'ati:cloud:graph:message',
					// 'ati:cloud:graph:commit',
				]
			: []),
	] as EntityATI[];

	const entities = supportedEntities.filter((supportedEntity) =>
		filteredEntities.includes(supportedEntity),
	);

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
		thirdPartyFilters,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const { finalExperimentId, finalShadowExperimentId, finalExperimentLayers } =
		getUserShardExperimentOverrides({
			isUserShardModel,
			isUserShardControl,
			experimentLayers,
			experimentId,
			shadowExperimentId,
			userShardL1LayerId,
		});

	// Default behaviour currently is to include the confluenceEntity hydration,
	// hence the control is to include it. But we check that the cohort is explicitly
	// 'test' to ensure that this user is actually in the experiment.
	const excludeConfluenceEntity =
		FeatureGates.getExperimentValue<string>(
			'remove_confluence_entity_hydration_experiment',
			'cohort',
			'control',
		) === 'test';

	const limit = isUserShardUI ? 10 : 7;

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit,
		commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters: {
			issueFilter: {
				projectARIs: [],
				assigneeARIs: [],
				reporterARIs: [],
				commenterARIs: [],
				issueTypeIDs: [],
				issueLabels: [],
				issueTypes: [],
				statusCategories: [],
				watcherARIs: [],
			},
		},
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId: finalExperimentId ?? undefined,
		shadowExperimentId: finalShadowExperimentId ?? undefined,
		experimentLayers: mapExperimentLayers(finalExperimentLayers),
		includeSocialSignals: false,
		includeUserSignals: isUserShardUI,
		includeSpaceSubtype: false,
		includeConfluenceEntity: !excludeConfluenceEntity,
		skipFieldsFor3pInQuickFind: quickFind3PExperiment.skip3pHydration,
		includeAutocompleteSuggestions: fg('fe_quickfind_autocomplete_suggestions'),
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			serveUserShardUI: isUserShardUI,
			headers,
		});

		const traceInfo = fg('rovo-search-vitalstats-tracing-headers')
			? extractTraceInfoFromResponse(response)
			: undefined;

		if (networkCallbacks) {
			const validationResult = validateConfluenceEntitiesResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		const autocompleteSuggestions = response.data?.search?.results.autocompleteSuggestions;
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
			fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
			errors: response.errors || [],
			autocompleteSuggestions,
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchEntitiesRequestId: undefined,
			fetchEntitiesTraceId: undefined,
			autocompleteSuggestions: undefined,
		};
	}
};

export const fetchConfluenceSpaces: (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchSpacesRequestId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
}> = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const { commonFilters, confluenceFilters, mercuryFilters, compassFilters, assetsFilters } =
		selectedFilters || getEmptySelectedFilters();

	const entities: EntityATI[] = ['ati:cloud:confluence:space'];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const isUserShardModel = serveUserShardModel();
	const isUserShardControl = serveUserShardControl();
	const userShardSpaceL1Id = getUserShardSpaceL1Id();
	const userShardL1LayerId = isUserShardModel ? (userShardSpaceL1Id ?? 'default') : 'default';
	const { finalExperimentId, finalShadowExperimentId, finalExperimentLayers } =
		getUserShardExperimentOverrides({
			isUserShardModel,
			isUserShardControl,
			experimentLayers,
			experimentId,
			shadowExperimentId,
			userShardL1LayerId,
		});

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 1,
		commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters: {
			issueFilter: {
				projectARIs: [],
				assigneeARIs: [],
				reporterARIs: [],
				commenterARIs: [],
				issueTypeIDs: [],
				issueLabels: [],
				issueTypes: [],
				statusCategories: [],
				watcherARIs: [],
			},
		},
		mercuryFilters,
		compassFilters,
		thirdPartyFilters: {
			titleMatchOnly: false,
			subtypes: [],
		},
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId: finalExperimentId ?? undefined,
		shadowExperimentId: finalShadowExperimentId ?? undefined,
		experimentLayers: mapExperimentLayers(finalExperimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			serveUserShardUI: false, // Spaces don't have user action data
			headers,
		});

		const traceInfo = fg('rovo-search-vitalstats-tracing-headers')
			? extractTraceInfoFromResponse(response)
			: undefined;

		if (networkCallbacks) {
			const validationResult = validateConfluenceSpacesResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchSpacesRequestId: response.extensions?.gateway?.request_id,
			errors: response.errors || [],
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchSpacesRequestId: undefined,
		};
	}
};

export const fetchAtlasEntities: (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchEntitiesRequestId: (string | null) | undefined;
	fetchEntitiesTraceId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
}> = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const { commonFilters, mercuryFilters, compassFilters, assetsFilters } =
		selectedFilters || getEmptySelectedFilters();

	const quickFind3PExperiment = serve3PinQuickFind(thirdPartyConfigs);
	const thirdPartyFilters: ThirdPartyFilters = quickFind3PExperiment.serveThirdPartyFilters
		? getThirdPartyFiltersForQuickFind(thirdPartyConfigs)
		: { titleMatchOnly: false, subtypes: [] };

	const entities: EntityATI[] = [
		'ati:cloud:townsquare:project',
		'ati:cloud:townsquare:goal',
		'ati:cloud:townsquare:tag',
		...(quickFind3PExperiment.serveGraphDocument
			? (['ati:cloud:graph:document'] as EntityATI[])
			: []),
		...(quickFind3PExperiment.serveGraphSmartlinks
			? (['ati:cloud:graph:remote-link'] as EntityATI[])
			: []),
		...(quickFind3PExperiment.serveAdditionalGraphEntities
			? ([
					'ati:cloud:graph:branch',
					'ati:cloud:graph:calendar-event',
					'ati:cloud:graph:design',
					'ati:cloud:graph:pull-request',
					'ati:cloud:graph:repository',
					'ati:cloud:graph:video',
					'ati:cloud:graph:work-item',
					// These entities are excluded to reduce the noise of 3p results in QF (https://hello.atlassian.net/wiki/x/SDhKYgE)
					// 'ati:cloud:graph:conversation',
					// 'ati:cloud:graph:message',
					// 'ati:cloud:graph:commit',
				] as EntityATI[])
			: []),
	];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 6,
		commonFilters,
		assetsFilters,
		confluenceFilters: {
			spacesFilter: [],
			contributorsFilter: [],
			ancestorIdsFilter: [],
			range: [],
			owners: [],
			labelsFilter: [],
			titleMatchOnly: true,
			contentStatuses: [],
			containerStatus: [],
		},
		jiraFilters: {
			issueFilter: {
				projectARIs: [],
				assigneeARIs: [],
				reporterARIs: [],
				commenterARIs: [],
				issueTypeIDs: [],
				issueLabels: [],
				issueTypes: [],
				statusCategories: [],
				watcherARIs: [],
			},
		},
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
		skipFieldsFor3pInQuickFind: quickFind3PExperiment.skip3pHydration,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			serveUserShardUI: false, // Atlas entities don't have user action data
			headers,
		});

		const traceInfo = fg('rovo-search-vitalstats-tracing-headers')
			? extractTraceInfoFromResponse(response)
			: undefined;

		if (networkCallbacks) {
			const validationResult = validateAtlasResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
			fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
			errors: response.errors || [],
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchEntitiesRequestId: undefined,
			fetchEntitiesTraceId: undefined,
		};
	}
};

export const fetchMercuryEntities: (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchEntitiesRequestId: (string | null) | undefined;
	fetchEntitiesTraceId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
}> = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		assetsFilters,
		thirdPartyFilters,
	} = selectedFilters || getEmptySelectedFilters();

	const passionfruitAskEntity: EntityATI = 'ati:cloud:passionfruit:ask';
	const entities: EntityATI[] = [
		'ati:cloud:mercury:focus-area',
		'ati:cloud:mercury:focus-area-status-update',
		...(fg('platform_passionfruit_search_onboarding_enabled') ? [passionfruitAskEntity] : []),
	];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 6,
		commonFilters: commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			serveUserShardUI: false, // Mercury entities don't have user action data
			headers,
		});

		const traceInfo = fg('rovo-search-vitalstats-tracing-headers')
			? extractTraceInfoFromResponse(response)
			: undefined;

		if (networkCallbacks) {
			const validationResult = validateMercuryResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
			fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
			errors: response.errors || [],
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchEntitiesRequestId: undefined,
			fetchEntitiesTraceId: undefined,
		};
	}
};

export const fetchTalentEntities: (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchEntitiesRequestId: (string | null) | undefined;
	fetchEntitiesTraceId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
}> = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		assetsFilters,
		thirdPartyFilters,
		talentFilters,
	} = selectedFilters || getEmptySelectedFilters();

	const entities: EntityATI[] = ['ati:cloud:radar:position'];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 6,
		commonFilters: commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		talentFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			serveUserShardUI: false, // Talent entities don't have user action data
			headers,
		});

		const traceInfo = fg('rovo-search-vitalstats-tracing-headers')
			? extractTraceInfoFromResponse(response)
			: undefined;

		if (networkCallbacks) {
			const validationResult = validateTalentResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
			fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
			errors: response.errors || [],
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchEntitiesRequestId: undefined,
			fetchEntitiesTraceId: undefined,
		};
	}
};

export const fetchAssetsEntities: (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchEntitiesRequestId: (string | null) | undefined;
	fetchEntitiesTraceId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
}> = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		assetsFilters,
		thirdPartyFilters,
	} = selectedFilters || getEmptySelectedFilters();

	const entities: EntityATI[] = [
		'ati:cloud:cmdb:object',
		'ati:cloud:cmdb:schema',
		'ati:cloud:cmdb:type',
	];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 6,
		commonFilters: commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			headers,
		});

		const traceInfo = fg('rovo-search-vitalstats-tracing-headers')
			? extractTraceInfoFromResponse(response)
			: undefined;

		if (networkCallbacks) {
			const validationResult = validateAssetsResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
			fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
			errors: response.errors || [],
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchEntitiesRequestId: undefined,
			fetchEntitiesTraceId: undefined,
		};
	}
};

/**
 * The experiment layers are mapped to ensure permitted attributes are sent into search requests.
 */
const mapExperimentLayers = (experimentLayers?: SearchExperimentLayer[]) =>
	experimentLayers?.map(({ name, layerId, shadowId, definitions }) => ({
		name,
		layerId,
		shadowId,
		definitions,
	}));

export const fetchJiraIssuesAndTypeEntities: (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	isJiraIssueIndexed: boolean | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	typeFilterEntities: UseTypeEntitiesResult,
	sessionId?: string,
	hasEditOrViewPlanPermission?: boolean,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchEntitiesRequestId: (string | null) | undefined;
	fetchEntitiesTraceId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
	autocompleteSuggestions: string[] | undefined;
}> = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	isJiraIssueIndexed: boolean | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	typeFilterEntities: UseTypeEntitiesResult,
	sessionId?: string,
	hasEditOrViewPlanPermission?: boolean,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	const {
		commonFilters,
		confluenceFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		jiraFilters,
		assetsFilters,
	} = selectedFilters || getEmptySelectedFilters();
	const { selectedEntities, entitiesForQuery } = typeFilterEntities;

	const gitHub3PExperiment = serveGitHub3PInJiraQuickFind(thirdPartyConfigs);

	const supportedEntities: EntityATI[] = [
		JIRA_ISSUE_ENTITY_ATI,
		JIRA_PROJECT_ENTITY_ATI,
		JIRA_BOARD_ENTITY_ATI,
		JIRA_FILTER_ENTITY_ATI,
		JIRA_DASHBOARD_ENTITY_ATI,
	];
	if (hasEditOrViewPlanPermission || !fg('rovo_search_check_plan_permission')) {
		supportedEntities.push(JIRA_PLAN_ENTITY_ATI);
	}

	const entities: EntityATI[] = selectedEntities.length
		? supportedEntities.filter((supportedEntity) => entitiesForQuery.includes(supportedEntity))
		: [JIRA_ISSUE_ENTITY_ATI];

	// GitHub entities are appended directly to the final entities array because they are
	// not part of Jira's type mapping in useTypeEntities, so they would be lost if included
	// in supportedEntities (filtered out on the type-filter path, absent on the default path).
	if (gitHub3PExperiment.serveGitHub3P) {
		entities.push(
			...DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
		);
	}

	const gitHubThirdPartyFilters: ThirdPartyFilters = gitHub3PExperiment.serveGitHub3P
		? getGitHubThirdPartyFiltersForQuickFind(thirdPartyConfigs)
		: thirdPartyFilters;

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
		isJiraIssueIndexed,
		thirdPartyFilters: gitHubThirdPartyFilters,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers } = backendExperiment || {};

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:jira::site/${cloudId}`,
		tenantId: cloudId,
		limit: fg('search_qf_increase_issues_in_jira') ? 12 : 7,
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters: gitHubThirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeJiraIssueType: true,
		includeSpaceSubtype: false,
		assetsFilters,
		skipFieldsFor3pInQuickFind: gitHub3PExperiment.serveGitHub3P,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			serveUserShardUI: false, // Jira entities don't have user action data
			headers,
		});

		const traceInfo = fg('rovo-search-vitalstats-tracing-headers')
			? extractTraceInfoFromResponse(response)
			: undefined;

		if (networkCallbacks) {
			const validationResult = validateJiraIssuesResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		const autocompleteSuggestions = response.data?.search?.results.autocompleteSuggestions;
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
			fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
			errors: response.errors || [],
			autocompleteSuggestions,
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchEntitiesRequestId: undefined,
			fetchEntitiesTraceId: undefined,
			autocompleteSuggestions: undefined,
		};
	}
};

export const fetchJiraEntities: (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	isJiraIssueIndexed: boolean | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	hasEditOrViewPlanPermission?: boolean,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	errors: import('@atlassian/search-client').AggError[];
	autocompleteSuggestions: string[] | undefined;
}> = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	isJiraIssueIndexed: boolean | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	hasEditOrViewPlanPermission?: boolean,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	const {
		commonFilters,
		confluenceFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		jiraFilters,
		assetsFilters,
	} = selectedFilters || getEmptySelectedFilters();

	const entities: EntityATI[] = [
		JIRA_BOARD_ENTITY_ATI,
		JIRA_FILTER_ENTITY_ATI,
		JIRA_PROJECT_ENTITY_ATI,
		JIRA_DASHBOARD_ENTITY_ATI,
	];

	if (hasEditOrViewPlanPermission || !fg('rovo_search_check_plan_permission')) {
		entities.push(JIRA_PLAN_ENTITY_ATI);
	}
	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
		isJiraIssueIndexed,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers } = backendExperiment || {};

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:jira::site/${cloudId}`,
		tenantId: cloudId,
		limit: 5,
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		assetsFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeJiraBoardContainer: true,
		includeSpaceSubtype: false,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			headers,
		});

		const traceInfo = fg('rovo-search-vitalstats-tracing-headers')
			? extractTraceInfoFromResponse(response)
			: undefined;

		if (networkCallbacks) {
			const validationResult = validateJiraEntitiesResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		const autocompleteSuggestions = response.data?.search?.results.autocompleteSuggestions;
		return {
			fetchResults: processAndValidateNodes(nodes),
			errors: response.errors || [],
			autocompleteSuggestions,
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			autocompleteSuggestions: undefined,
		};
	}
};

/**
 * Validates and maps search result nodes to essential data format.
 * Returns null for invalid nodes.
 */
/**
 * Essential data format for search results after validation and mapping.
 */
export type EssentialSearchResult = {
	id: string;
	title: string;
	data: SearchPageNode;
	type: 'search-result';
};

export const mapEssentialData: (result: SearchPageNode) => EssentialSearchResult | null = (
	result: SearchPageNode,
): EssentialSearchResult | null => {
	// Detect and filter out invalid/empty nodes from API
	// Allow empty strings but not null/undefined
	if (!result || !result.id || result.title == null) {
		return null;
	}

	return {
		id: result.id,
		title: result.title,
		data: result,
		type: 'search-result' as const,
	};
};

/**
 * Processes nodes and filters out invalid ones.
 */
const processAndValidateNodes = (nodes: SearchPageNode[]) => {
	const mappedNodes = nodes.map(mapEssentialData);
	const validNodes = mappedNodes.filter((node): node is NonNullable<typeof node> => node !== null);

	return validNodes;
};
